import { ClinicalEncounterActions } from "./ClinicalEncounterActions";
import dayjs from "dayjs";
import { useOrganizationContext } from "../../../../../contexts/OrganizationContext";
import { currentFeatures } from "@/configs/Settings";
import { EhrMobileGenericSection } from "../../mobile/EhrMobileGenericSection";
import {
  resolveAttendanceSpecialty,
  resolveAttendanceTypeLabel,
} from "@/utils/resolveAttendanceSpecialty";
import {
  encounterOwnerPractitioner,
  isTeleconsultoriaEncounter,
  isTeleconsultoriaParticipant,
  teleconsultoriaGuestFromParticipants,
} from "src-next/modules/teleconsultoria";

interface IProps {
  title: string;
  data: any;
  openVisualizer: any;
  verifyVisualizer: any;
}

export function ClinicalEncounter({
  title,
  data,
  openVisualizer,
  verifyVisualizer,
}: IProps) {
  const { organizationList } = useOrganizationContext();

  // A soma das larguras deve ficar abaixo de 100% (hoje 86%).
  // A coluna "Ações" não tem largura própria: ela ocupa o espaço restante
  // da tabela (tableLayout fixed). Com 100%, os botões de ação somem.
  const columns = [
    {
      column: "Organização",
      rowName: "organization",
      width: "18%",
      lineClamp: 2,
    },
    {
      column: "Especialidade",
      rowName: "serviceType",
      width: "12%",
    },
    {
      column: "Tipo de atendimento",
      rowName: "attendanceType",
      width: "12%",
    },
    {
      column: "Profissional",
      rowName: "practitioner",
      width: "17%",
      lineClamp: 2,
    },
    // "-" indica que o atendimento não teve teleconsultoria.
    {
      column: "Teleconsultor",
      rowName: "teleconsultor",
      width: "17%",
      lineClamp: 2,
    },
    { column: "Data", rowName: "date", width: "10%" },
  ];

  // entered-in-error: Encounter órfão do teleconsultor (falhou antes do partOf). Não exibe.
  const rowData = data
    ?.filter((el: any) => el?.encounter?.status !== "entered-in-error")
    .map((el: any) => {
    const encounter = el?.encounter;
    const questionnaire = el?.questionnaire?.[0]?.resource;
    // Host = primeiro Practitioner sem a marcação de teleconsultor.
    const hostParticipants = encounter?.participant?.filter(
      (item: any) => !isTeleconsultoriaParticipant(item),
    );
    const guest = teleconsultoriaGuestFromParticipants(encounter?.participant);
    const teleconsultorName = guest.practitioner?.display?.toUpperCase();
    // Encounter do teleconsultor (partOf): mesmo Appointment do host, linha própria.
    // Profissional = teleconsultor; Tipo = "Teleconsultoria"; coluna Teleconsultor = "-".
    const isGuestEncounter = isTeleconsultoriaEncounter(encounter);
    const ownerName = isGuestEncounter
      ? encounterOwnerPractitioner(encounter)?.display?.toUpperCase()
      : undefined;

    return {
      appointmentId:
        el?.encounter?.appointment?.[0]?.reference?.split("/")?.[1],
      partOf: encounter?.partOf?.reference,
      // Dono do Encounter (teleconsultor quando há partOf). Usado no canEdit.
      ownerReference: encounterOwnerPractitioner(encounter)?.reference,
      subject: currentFeatures.defaultApp
        ? encounter?.subject?.reference?.split("-")?.[1]
        : encounter?.subject?.reference?.split("/")?.[1],
      encounterId: el?.encounter?.id,
      participant: el?.encounter?.participant,
      organizationId: encounter?.serviceProvider.reference.split("-")[1],
      status: el?.encounter?.status,
      organization:
        organizationList?.find((el: any) =>
          el.resource.id.includes(
            encounter?.serviceProvider.reference.split("/")[1]
          )
        )?.resource?.name ||
        encounter?.serviceProvider.reference.split("-")[1],
      serviceType: resolveAttendanceSpecialty({ encounter }),
      attendanceType: isGuestEncounter
        ? "Teleconsultoria"
        : resolveAttendanceTypeLabel(encounter),
      date: dayjs(encounter?.actualPeriod?.start).format("DD/MM/YYYY"),
      questionnarie: questionnaire?.item,
      observations: el?.obs,
      practitioner:
        ownerName ||
        hostParticipants
          ?.find((el: any) => el?.actor?.reference?.includes("Practitioner"))
          ?.actor?.display?.toUpperCase() ||
        hostParticipants?.find((el: any) =>
          el?.actor?.reference?.includes("Practitioner")
        )?.actor?.value ||
        hostParticipants?.find((el: any) =>
          el?.actor?.type?.includes("Practitioner")
        )?.actor?.identifier?.value,
      teleconsultor: isGuestEncounter ? "-" : teleconsultorName || "-",
    };
  });

  return (
    <EhrMobileGenericSection
      title={title}
      columns={columns}
      rows={rowData ?? []}
      actions={(rowData: any) => (
        <ClinicalEncounterActions
          verifyVisualizer={verifyVisualizer}
          openVisualizer={openVisualizer}
          row={rowData}
        />
      )}
    />
  );
}
